/**
 * Luckblocks dialog: the brand replacement for SweetAlert2.
 *
 * It keeps the subset of the SweetAlert API the app used, so call sites only change
 * their import:
 *   lbDialog.fire({ title, text | html, icon, timer, showConfirmButton, ... }) -> Promise
 *   lbDialog.fire(title, text, icon)
 *   lbDialog.showLoading()   turns the open dialog into a loading state (skull coin spinner)
 *   lbDialog.close()
 * The promise resolves with { isConfirmed, isDismissed, dismiss } like SweetAlert's.
 *
 * It is plain DOM rather than React because it is called from hooks, contexts and
 * event handlers that have no component tree to render into. Styles live in index.css
 * under "Dialog" (.lb-dialog-*).
 */
import cranniCoin from '../../../assets/images/cranni-coin.webp';
import en from '../../../i18n/locales/en.json';
import pt from '../../../i18n/locales/pt.json';
import es from '../../../i18n/locales/es.json';
import zh from '../../../i18n/locales/zh.json';

const BUNDLES = { en, pt, es, zh };

const label = (key) => {
  const lang = document.documentElement.lang || 'en';
  const bundle = BUNDLES[lang] || en;
  return (bundle.common && bundle.common[key]) || en.common[key];
};

const ICONS = {
  success: '<path d="M5 12.5l4.2 4.2L19 7" />',
  error: '<path d="M7 7l10 10M17 7L7 17" />',
  warning: '<path d="M12 7v6" /><path d="M12 17h.01" />',
  info: '<path d="M12 11v6" /><path d="M12 7h.01" />',
  question: '<path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14" /><path d="M12 17.5h.01" />',
};

let current = null;

function iconMarkup(icon) {
  if (!ICONS[icon]) return '';
  return `<div class="lb-dialog-icon" data-icon="${icon}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[icon]}</svg></div>`;
}

function loaderMarkup() {
  return `<div class="lb-dialog-loader" aria-hidden="true"><span class="lb-dialog-loader-ring"></span><img src="${cranniCoin}" alt="" /></div>`;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function finish(result) {
  if (!current) return;
  const { root, resolve, timerId, onKey, previousFocus } = current;
  current = null;
  clearTimeout(timerId);
  document.removeEventListener('keydown', onKey);
  root.classList.add('is-leaving');
  setTimeout(() => root.remove(), 160);
  if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
  resolve(result);
}

const confirmResult = { isConfirmed: true, isDismissed: false, dismiss: undefined };
const dismissResult = (reason) => ({ isConfirmed: false, isDismissed: true, dismiss: reason });

function fire(options, text, icon) {
  const opts = typeof options === 'object' && options !== null ? options : { title: options, text, icon };
  // A new dialog replaces the open one, as SweetAlert does.
  if (current) finish(dismissResult('replaced'));

  const {
    title,
    html,
    timer,
    timerProgressBar,
    showConfirmButton = true,
    showCancelButton = false,
    confirmButtonText = label('ok'),
    cancelButtonText = label('cancel'),
    allowOutsideClick = true,
    allowEscapeKey = true,
    didOpen,
    willOpen,
  } = opts;

  const root = document.createElement('div');
  root.className = 'lb-dialog-root';
  root.innerHTML = `
    <div class="lb-dialog-backdrop"></div>
    <div class="lb-dialog" role="dialog" aria-modal="true" aria-labelledby="lb-dialog-title" tabindex="-1">
      <div class="lb-dialog-visual">${iconMarkup(opts.icon)}</div>
      ${title ? `<h2 class="lb-dialog-title" id="lb-dialog-title">${escapeHtml(title)}</h2>` : ''}
      ${html ? `<div class="lb-dialog-text">${html}</div>` : opts.text ? `<p class="lb-dialog-text">${escapeHtml(opts.text)}</p>` : ''}
      <div class="lb-dialog-actions">
        ${showCancelButton ? `<button type="button" class="lb-btn lb-btn-ghost" data-action="cancel">${escapeHtml(cancelButtonText)}</button>` : ''}
        ${showConfirmButton ? `<button type="button" class="lb-btn lb-btn-primary" data-action="confirm">${escapeHtml(confirmButtonText)}</button>` : ''}
      </div>
      ${timer && timerProgressBar ? `<div class="lb-dialog-timer" style="animation-duration:${timer}ms"></div>` : ''}
    </div>`;

  return new Promise((resolve) => {
    const onKey = (event) => {
      if (event.key === 'Escape' && allowEscapeKey && !root.classList.contains('is-loading')) finish(dismissResult('esc'));
    };
    current = { root, resolve, onKey, previousFocus: document.activeElement, timerId: null };

    root.querySelector('[data-action="confirm"]')?.addEventListener('click', () => finish(confirmResult));
    root.querySelector('[data-action="cancel"]')?.addEventListener('click', () => finish(dismissResult('cancel')));
    root.querySelector('.lb-dialog-backdrop').addEventListener('click', () => {
      const outside = typeof allowOutsideClick === 'function' ? allowOutsideClick() : allowOutsideClick;
      if (outside && !root.classList.contains('is-loading')) finish(dismissResult('backdrop'));
    });
    document.addEventListener('keydown', onKey);

    if (willOpen) willOpen(root);
    document.body.appendChild(root);
    if (!root.querySelector('.lb-dialog-actions').children.length) root.querySelector('.lb-dialog-actions').remove();
    if (timer) current.timerId = setTimeout(() => finish(dismissResult('timer')), timer);
    (root.querySelector('[data-action="confirm"]') || root.querySelector('.lb-dialog')).focus?.();
    if (didOpen) didOpen(root);
  });
}

/** Swaps the icon for the skull coin spinner and hides the buttons until the next fire(). */
function showLoading() {
  if (!current) {
    fire({ showConfirmButton: false, allowOutsideClick: false });
  }
  const { root } = current;
  root.classList.add('is-loading');
  root.querySelector('.lb-dialog-visual').innerHTML = loaderMarkup();
  root.querySelector('.lb-dialog-actions')?.remove();
}

function close() {
  finish(dismissResult('close'));
}

const lbDialog = { fire, showLoading, close, isVisible: () => Boolean(current) };

export default lbDialog;
